import { uniq } from "lodash";
import { Moment } from "moment";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import SmoothCollapse from "react-smooth-collapse";

import {
  getAssignmentCourseId,
  getAssignmentDueDate,
  getAssignmentExamQuestionIds,
  getAssignmentName,
  getAssignmentSpecType,
  getAssignmentWordIds
} from "seneca-common/features/assignments/features/student-assignments/state";
import { COURSE_EXAM_QUESTIONS_SPEC_TYPE } from "seneca-common/features/assignments/state/models/spec/CourseExamQuestionsSpec";
import { VOCABULARY_SPEC_TYPE } from "seneca-common/features/assignments/state/models/spec/VocabularySpec";
import useEQCourseSections from "seneca-common/features/exam-questions/hooks/useEQCourseSections";
import useFetchWords from "seneca-common/features/vocabulary/words/hooks/useFetchWords";
import { getRelativeDateAndTime } from "seneca-common/utils/functions/formatting/getRelativeDate";
import {
  useMemoSelector,
  useSelectorOnConditionWithArgs,
  useSelectorWithArgs
} from "seneca-common/utils/hooks/selectors";

import { getExamQuestionsInAssignmentSections } from "features/assignments/common/utils";
import { makeGetAssignmentSectionContentsInCourseOrder } from "features/assignments/selectors/getAssignmentSectionIdsInCourseOrder";
import { useGetExamQuestionsData } from "features/classroom/pages/classroom/components/EndSession/assignment-progress/components/useGetExamQuestionsData";
import {
  collapseAssignment,
  expandAssignment,
  isAssignmentExpanded
} from "features/classroom/state";
import { Var } from "features/common/localisation";

import { SectionRowChevron } from "../../../common";
import AssignmentProgressBar from "../AssignmentProgressBar";
import AssignmentQuestionRow from "../AssignmentQuestionRow";
import AssignmentSectionRow from "../AssignmentSectionRow";
import AssignmentWordRow from "../AssignmentWordRow";
import styles from "./AssignmentAccordion.module.scss";
import AssignmentHeader from "./AssignmentHeader";
import RelatedExamQuestionsLink from "./components/RelatedExamQuestionsLink";
import { LinkToExamQuestionSession } from "./LinkToExamQuestionSession";

type Props = {
  activeSectionId: string;
  activeWordId: string;
  assignmentId: string;
  now: Moment;
};

export default function AssignmentAccordion({
  activeSectionId,
  activeWordId,
  assignmentId,
  now
}: Props) {
  const dispatch = useDispatch();

  const sectionContents = useMemoSelector(
    makeGetAssignmentSectionContentsInCourseOrder,
    assignmentId
  );

  const questionIds = useSelectorWithArgs(
    getAssignmentExamQuestionIds,
    assignmentId
  );

  const specType = useSelectorWithArgs(getAssignmentSpecType, assignmentId);
  const expanded = useSelectorWithArgs(isAssignmentExpanded, assignmentId);
  const dueDate = useSelectorWithArgs(getAssignmentDueDate, assignmentId);
  const name = useSelectorWithArgs(getAssignmentName, assignmentId);
  const relativeDate = getRelativeDateAndTime(dueDate, now);

  const handleExpand = useCallback(() => {
    if (expanded) dispatch(collapseAssignment());
    else dispatch(expandAssignment(assignmentId));
  }, [dispatch, expanded, assignmentId]);

  const courseId = useSelectorWithArgs(getAssignmentCourseId, assignmentId);

  const { data: courseExamQuestions } = useEQCourseSections({ courseId });

  const promoExamQuestionsRelatedToAssignment =
    getExamQuestionsInAssignmentSections({
      courseExamQuestions,
      assignmentSectionIds: uniq(
        sectionContents.map(sectionContent => sectionContent.sectionId).toJS()
      ) as string[],
      courseId
    });

  const {
    score,
    scoreRatioDisplay,
    numOfQuestionsAssigned,
    questionIdsToStudy
  } = useGetExamQuestionsData(assignmentId, courseId!);

  const eqOnly = specType === COURSE_EXAM_QUESTIONS_SPEC_TYPE;
  const isVocabularyAssignment = specType === VOCABULARY_SPEC_TYPE;

  const assignmentWordIds = useSelectorOnConditionWithArgs(
    isVocabularyAssignment,
    getAssignmentWordIds,
    assignmentId
  );

  const { data: words, isLoading: isWordsLoading } = useFetchWords({
    fetchParams: {
      wordIds: assignmentWordIds?.toArray() ?? []
    },
    enabled: isVocabularyAssignment
  });

  return (
    <div className={styles.wrapper}>
      <AssignmentHeader onClick={handleExpand}>
        {{
          name: <Var>{name}</Var>,
          chevron: (
            <SectionRowChevron rotate={expanded} className={styles.chevron} />
          ),
          dueDate: (
            <>
              Due: <Var>{relativeDate}</Var>
            </>
          ),
          progress: <AssignmentProgressBar assignmentId={assignmentId} />
        }}
      </AssignmentHeader>
      <SmoothCollapse expanded={expanded}>
        {eqOnly &&
          questionIds.map((questionId, index) => (
            <AssignmentQuestionRow
              key={questionId}
              questionId={questionId}
              assignmentId={assignmentId}
              number={index + 1}
            />
          ))}
        {isVocabularyAssignment &&
          !isWordsLoading &&
          words?.map(({ wordId }, index) => (
            <AssignmentWordRow
              key={wordId}
              word={words[index]}
              number={index + 1}
              assignmentId={assignmentId}
              activeWordId={activeWordId}
            />
          ))}
        {sectionContents.map(({ sectionId, contentType }) => (
          <AssignmentSectionRow
            key={sectionId}
            assignmentId={assignmentId}
            activeSectionId={activeSectionId}
            sectionId={sectionId}
            contentType={contentType}
          />
        ))}
        {!eqOnly && numOfQuestionsAssigned > 0 ? (
          <LinkToExamQuestionSession
            assignedQuestionIds={questionIdsToStudy as string[]}
            courseId={courseId}
            assignmentId={assignmentId}
            score={score}
            scoreRatioDisplay={scoreRatioDisplay}
            numOfQuestionsAssigned={numOfQuestionsAssigned}
            numOfQuestionsToStudy={questionIdsToStudy.length}
          />
        ) : (
          promoExamQuestionsRelatedToAssignment &&
          promoExamQuestionsRelatedToAssignment.length > 0 &&
          courseId && (
            <RelatedExamQuestionsLink
              examQuestionsInAssignmentSections={
                promoExamQuestionsRelatedToAssignment
              }
              courseId={courseId}
            />
          )
        )}
      </SmoothCollapse>
    </div>
  );
}
